<script>
import axios from 'axios';
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';
import debounce from 'lodash/debounce';
import partition from 'lodash/partition';
import uniq from 'lodash/uniq';
import {mapGetters, mapMutations} from 'vuex';
import {PdlLoading} from '@pedal/pdl-loading';
import {PdlCallout} from '@pedal/pdl-callout';
import TrekButton from '@/components/TrekButton';
import CartSummary from '@/components/cart/CartSummary';
import {PdlTabPane, PdlTabs} from '@pedal/pdl-tabs';
import {PdlCollapse} from '@pedal/pdl-collapse';
import Warehouse from '@/components/containers/checkout/Warehouse';
import ElDialog from '@/components/Dialog';
import warehouse from '@/mixins/warehouse';
import {flattenAllocatedItems} from '@/utils/allocated-items-helpers';
import {CheckoutApi} from '@/api/checkout';
import CheckoutNavigation from '@/components/checkout/CheckoutNavigation';

export default {
  components: {
    PdlLoading,
    PdlCallout,
    TrekButton,
    CartSummary,
    PdlTabs,
    PdlTabPane,
    PdlCollapse,
    Warehouse,
    ElDialog,
    CheckoutNavigation,
  },

  mixins: [warehouse],

  provide() {
    return {
      container: this,
    };
  },

  props: {
    textBikes: {
      type: String,
      default: 'Bikes',
    },
    textAccessories: {
      type: String,
      default: 'Accessories',
    },
  },

  data() {
    return {
      cartData: {
        bikes: {
          warehouses: [],
        },
        parts: {
          warehouses: [],
        },
        cartSummary: {},
      },
      calculatedFreight: {
        bikes: false,
        parts: false,
      },
      loading: true,
      showSaveWarning: false,
      types: ['bikes', 'parts'],
      selectedTabName: '',
      accessoriesTabChecked: false,
      dialogVisible: false,
      targetUrl: '',
      hasCheckedAllocatedItems: false,
    };
  },

  computed: {
    bikes() {
      return this.cartData.bikes;
    },

    parts() {
      return this.cartData.parts;
    },

    bikeWarehouseIds() {
      return this.getWarehouseIds('bikes');
    },

    partWarehouseIds() {
      return this.getWarehouseIds('parts');
    },

    tabSelected: {
      get() {
        return this.selectedTabName || (!isEmpty(this.bikes) && this.bikes.totalAllocatedQty ? 'bikes' : 'parts');
      },
      set(name) {
        this.selectedTabName = name;
      },
    },

    isHoldOrder() {
      return (this.bikes && this.bikes.hold) || (this.parts && this.parts.hold);
    },

    isHoldBikesOnly() {
      return this.bikes && this.bikes.hold && (!this.parts || !this.parts.hold);
    },

    isHoldPartsOnly() {
      return (!this.bikes || !this.bikes.hold) && this.parts && this.parts.hold;
    },

    isHoldAllOrder() {
      return this.bikes && this.bikes.hold && this.parts && this.parts.hold;
    },

    bikesAndParts() {
      return {bikes: this.bikes, parts: this.parts};
    },

    showReviewLink() {
      return (
        this.isHoldAllOrder ||
        (this.isHoldBikesOnly && !this.hasAllocatedParts) ||
        (this.isHoldPartsOnly && !this.hasAllocatedBikes)
      );
    },

    hasAllocatedBikes() {
      return this.bikes && this.bikes.totalAllocatedQty;
    },

    hasAllocatedParts() {
      return this.parts && this.parts.totalAllocatedQty;
    },

    ...mapGetters('backend', ['consumerFriendlyMode']),
    ...mapGetters('storage', ['hasUserTouchedAllocatedItems']),
  },

  async mounted() {
    await this.loadCart();
    if (!this.hasCheckedAllocatedItems && !this.hasUserTouchedAllocatedItems) {
      await this.loadTotals();
    }
  },

  created() {
    // Totals should include all selected allocated items
    // Optimize requests to server if user check items one by one
    this.debouncedLoadTotals = debounce(this.loadTotals, 400);
  },

  methods: {
    cartSummaryLoaded(cartSummaryEl) {
      if (cartSummaryEl) {
        $(cartSummaryEl).find('.js-shipments-listener').click($.proxy(this.proceedToOrderReview, this));
      }
    },

    proceedToUrl() {
      window.location = this.targetUrl;
    },

    proceedToOrderReview(event) {
      const target = event?.currentTarget;
      this.targetUrl = target?.dataset?.link ?? target?.href;

      event.preventDefault();

      if (
        this.targetUrl.includes('order-review') &&
        !this.accessoriesTabChecked &&
        this.bikes.totalAllocatedQty &&
        this.parts.totalAllocatedQty
      ) {
        this.dialogVisible = true;
      } else {
        this.updateCartAllocatedItems();
      }
    },

    updateCartAllocatedItems() {
      this.dialogVisible = false;
      this.showSaveWarning = false;
      this.loading = true;

      this.storeUserSelection();

      return CheckoutApi.updateAllocatedItems(this.getUpdateAllocatedItemsDto())
        .then((data) => {
          if (data?.meta?.feedback?.message?.basePropertyValue) {
            this.showSaveWarning = true;
            this.loading = false;
          } else {
            this.proceedToUrl();
          }
        })
        .catch(() => {
          this.showSaveWarning = true;
          this.loading = false;
        });
    },

    // Temporary solution to map old data contract to new one
    // Remove in TBC-20362 and use new data contract directly
    getUpdateAllocatedItemsDto() {
      return Object.values(this.cartData).flatMap((value) => {
        return (
          value?.warehouses?.flatMap((warehouse) => {
            return (warehouse.allocatedItems || [])
              .filter((item) => item.selected)
              .map((item) => ({
                id: item.id,
                orderNumber: item.orderNumber,
                orderType: item.orderType,
                orderCompany: item.orderCompany,
                orderLineNumber: item.orderLineNumber,
                sku: item.sku,
                warehouse: warehouse.id,
                quantityAllocatedOrShipped: item.quantityAllocatedOrShipped,
                basePrice: {
                  formattedValue: item.unitPrice,
                },
              }));
          }) ?? []
        );
      });
    },

    tabClicked(paneName) {
      this.tabSelected = paneName;
      if (paneName == 'parts') this.accessoriesTabChecked = true;
    },

    changeGridTab(tab) {
      this.tabSelected = tab;
      if (tab == 'parts') this.accessoriesTabChecked = true;

      $('html, body').animate(
        {
          scrollTop: $(this.$refs.checkoutTabs.$el).offset().top - 150,
        },
        500
      );
    },

    getWarehouseIds(type) {
      if (!this[type] || !this[type].warehouses) return [];

      return this[type].warehouses.reduce((allWarehouseIds, warehouse) => {
        return [...allWarehouseIds, `${type}-${warehouse.id}`];
      }, []);
    },

    submitForm() {
      console.table(this.cartData.bikes);
    },

    getTabText(productType) {
      if (productType === 'bikes') return this.textBikes;
      if (productType === 'parts') return this.textAccessories;

      return '';
    },

    getFreightWarehouseData(warehouses) {
      if (isEmpty(warehouses)) return [];

      return map(warehouses, (warehouse) => {
        return {
          id: warehouse.id,
          isBackOrdered: false,
        };
      });
    },

    getFreightObject() {
      let data = {bikes: {}, parts: {}};

      data['isAllocatedItemsPage'] = true;
      if (this.cartData.bikes) {
        data.bikes['warehouses'] = this.getFreightWarehouseData(this.bikes.warehouses);
      }

      if (this.cartData.parts) {
        data.parts['warehouses'] = this.getFreightWarehouseData(this.parts.warehouses);
      }
      return data;
    },

    getTotalsWithAllocatedDto() {
      const allocatedItems = flattenAllocatedItems(this.cartData);
      const [selectedItems, unselectedItems] = partition(allocatedItems, (item) => item.selected);
      const selectedAllocatedItemDto = selectedItems.map((item) =>
        this.getAllocatedItemDto(item.quantityAllocatedOrShipped, item.lineSubtotalRaw, item.productType, item.sku)
      );
      // FE should send dto with 0 quantity and total in order to receive 0 subtotals
      const uniqueProductTypes = uniq(unselectedItems.map((item) => item.productType));
      const unselectedAllocatedItemsDto = uniqueProductTypes.map((productType) =>
        this.getAllocatedItemDto(0, 0, productType)
      );

      return [...selectedAllocatedItemDto, ...unselectedAllocatedItemsDto];
    },

    getAllocatedItemDto(quantity, totalPrice, groupingType, sku) {
      return {
        quantity,
        totalPrice,
        groupingType: {
          Bikes: 'BIKE',
          Accessories: 'AFTERMARKET',
        }[groupingType],
        ...(sku && {sku}),
      };
    },

    recalculateFreight(cartType) {
      axios
        .put('/cart/freight', this.getFreightObject())
        .then(() => {
          this.loadTotals().finally(() => {
            this.calculatedFreight[cartType] = true;
          });
        })
        .catch((error) => console.log(error));
    },

    loadCart() {
      return axios
        .get('/checkout-b2b/api/allocated-items/items')
        .then(({data}) => {
          this.cartData = data;
          const allocatedItems = flattenAllocatedItems(data);
          this.hasCheckedAllocatedItems = allocatedItems.some((allocatedItem) => allocatedItem.selected);

          // Preselect all items by default if user hasn't saved selection
          if (!this.hasCheckedAllocatedItems && !this.hasUserTouchedAllocatedItems) {
            allocatedItems.forEach((allocatedItem) => (allocatedItem.selected = true));
          }
        })
        .catch((error) => console.log(error))
        .finally(() => {
          this.loading = false;
        });
    },

    loadTotals() {
      return CheckoutApi.getTotalsWithAllocated(this.getTotalsWithAllocatedDto()).then((data) => {
        this.cartData.cartSummary.total = data.prices.totalPrice.formattedValue;

        const convertResponseItems = [
          [this.cartData.cartSummary.bikes, data.subTotals.BIKE],
          [this.cartData.cartSummary.parts, data.subTotals.AFTERMARKET],
        ];

        convertResponseItems.forEach(([componentData, responseData]) => {
          if (responseData) {
            componentData.totalItems = responseData.quantity;
            for (const [priceType, price] of Object.entries(responseData.prices)) {
              componentData[priceType] = price.formattedValue || componentData[priceType];
            }
          }
        });
      });
    },

    storeUserSelection() {
      const allocatedItems = flattenAllocatedItems(this.cartData);
      const isAllUnselected = allocatedItems.every((item) => !item.selected);

      // If user changed initial selection and proceed to Review Order
      // we should not preselect all items when user come back to Manage allocated items
      if (isAllUnselected && !this.hasUserTouchedAllocatedItems) {
        this.addUserTouchedAllocatedItems();
      }
    },

    navigateTo(url) {
      window.location.href = url;
    },

    ...mapMutations('storage', ['addUserTouchedAllocatedItems']),
  },
};
</script>